"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Link from "next/link";
import { ArticleLink } from "@/components/articles/article-link";
import { Coins, DollarSign, ArrowRight, PartyPopper } from "lucide-react";
const EXCHANGE_RATE = 0.50;
const PLATFORM_FEE_RATE = 0.07;
function calcPayout(coins: number) {
  const gross = coins * EXCHANGE_RATE;
  const fee = gross * PLATFORM_FEE_RATE;
  const net = gross - fee;
  return {
    gross: Number(gross.toFixed(2)),
    fee: Number(fee.toFixed(2)),
    net: Number(net.toFixed(2))
  };
}
export function MonetizationDemo() {
  const $ = _c(57);
  const [coinInput, setCoinInput] = useState("500");
  const [showSuccess, setShowSuccess] = useState(false);
  const parsedCoins = Math.max(0, Math.floor(Number(coinInput) || 0));
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== coinInput || $[1] !== parsedCoins || $[2] !== showSuccess) {
    const payout = calcPayout(parsedCoins);
    const handleCashout = () => {
      if (payout.net > 0) {
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 4000);
      }
    };
    t9 = "mx-auto w-full max-w-6xl px-6 pt-24 pb-12 lg:px-12 flex flex-col md:flex-row gap-12 items-center justify-between";
    t7 = "relative w-full max-w-2xl flex-1 rounded-3xl border border-border bg-gradient-to-tr from-indigo-900/10 via-background to-purple-900/10 shadow-2xl p-4 overflow-hidden before:absolute before:inset-0 before:bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] before:from-brand/5 before:via-transparent before:to-transparent before:pointer-events-none";
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] bg-[size:20px_20px] opacity-[0.03]" />;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    t0 = "bg-card w-full overflow-hidden rounded-2xl border border-border shadow-inner relative font-sans p-6 sm:p-8";
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="text-center mb-6"><div className="mb-3 flex justify-center"><div className="h-14 w-14 bg-brand/10 text-brand rounded-full flex items-center justify-center"><Coins className="h-7 w-7" /></div></div><p className="text-sm font-semibold text-brand uppercase tracking-wider mb-1">Earnings Estimator</p><h3 className="text-xl font-bold text-foreground">See What You Could Earn</h3></div>;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    let t10;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <label className="block text-sm font-medium text-muted-foreground mb-2">Your coin balance</label>;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <Coins className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />;
      $[16] = t11;
    } else {
      t11 = $[16];
    }
    let t12;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = e => {
        setCoinInput(e.target.value);
        setShowSuccess(false);
      };
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    let t13;
    if ($[18] !== coinInput) {
      t13 = <div className="relative flex-1">{t11}<input type="number" min={0} value={coinInput} onChange={t12} className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand/50" placeholder="Enter coins" /></div>;
      $[18] = coinInput;
      $[19] = t13;
    } else {
      t13 = $[19];
    }
    let t14;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = [100, 500, 1000].map(preset => <button key={preset} type="button" onClick={() => {
        setCoinInput(String(preset));
        setShowSuccess(false);
      }} className="shrink-0 text-xs font-semibold text-accent border border-accent/30 bg-accent/5 hover:bg-accent/10 px-2.5 py-2.5 rounded-xl transition-colors">{preset}</button>);
      $[20] = t14;
    } else {
      t14 = $[20];
    }
    if ($[21] !== t13) {
      t2 = <div className="mb-5">{t10}<div className="flex items-center gap-2">{t13}{t14}</div></div>;
      $[21] = t13;
      $[22] = t2;
    } else {
      t2 = $[22];
    }
    let t15;
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <span>Gross US Value</span>;
      $[23] = t15;
    } else {
      t15 = $[23];
    }
    const t16 = <div className="flex justify-between items-center text-muted-foreground">{t15}<span>${payout.gross.toFixed(2)}</span></div>;
    let t17;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="flex items-center gap-1.5">Platform Fee (7%)<span className="text-[10px] bg-muted rounded-full px-1.5 py-0.5">incl. Stripe fees</span></span>;
      $[24] = t17;
    } else {
      t17 = $[24];
    }
    const t18 = <div className="flex justify-between items-center text-muted-foreground">{t17}<span>-${payout.fee.toFixed(2)}</span></div>;
    let t19;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <span>Net Payout</span>;
      $[25] = t19;
    } else {
      t19 = $[25];
    }
    const t20 = <div className="pt-3 border-t border-border flex justify-between items-center font-bold text-foreground text-base">{t19}<span className="text-brand">${payout.net.toFixed(2)}</span></div>;
    if ($[26] !== t16 || $[27] !== t18 || $[28] !== t20) {
      t3 = <div className="bg-muted/30 rounded-xl p-4 mb-5 space-y-3 text-sm">{t16}{t18}{t20}</div>;
      $[26] = t16;
      $[27] = t18;
      $[28] = t20;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <p className="text-[11px] text-muted-foreground text-center mb-4">The 7% fee covers Stripe processing fees and helps keep IlorisNovel's servers running.</p>;
      $[30] = t4;
    } else {
      t4 = $[30];
    }
    const t21 = payout.net <= 0;
    let t22;
    let t23;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <DollarSign className="h-5 w-5" />;
      t23 = <span>Cash Out Now</span>;
      $[31] = t22;
      $[32] = t23;
    } else {
      t22 = $[31];
      t23 = $[32];
    }
    if ($[33] !== handleCashout || $[34] !== t21) {
      t5 = <button onClick={handleCashout} disabled={t21} className="w-full bg-accent hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-full shadow-lg shadow-accent/30 transition-colors flex items-center justify-center gap-2 cursor-pointer">{t22}{t23}</button>;
      $[33] = handleCashout;
      $[34] = t21;
      $[35] = t5;
    } else {
      t5 = $[35];
    }
    t6 = showSuccess && <div className="absolute inset-0 bg-background/80 backdrop-blur-md flex items-center justify-center z-10 animate-in fade-in duration-300 rounded-2xl"><div className="bg-card border border-primary/30 rounded-2xl p-6 shadow-2xl max-w-xs text-center mx-4"><div className="mb-3 flex justify-center"><div className="h-14 w-14 bg-brand/15 text-brand rounded-full flex items-center justify-center"><PartyPopper className="h-7 w-7" /></div></div><h4 className="text-lg font-bold text-foreground mb-1">Money Sent!</h4><p className="text-2xl font-extrabold text-brand mb-2">${payout.net.toFixed(2)}</p><p className="text-sm text-muted-foreground mb-4">has been sent straight to your bank account. <span className="italic">(demo)</span></p><p className="text-xs text-muted-foreground/80">Create an account and start publishing to earn real money from your stories.</p></div></div>;
    $[0] = coinInput;
    $[1] = parsedCoins;
    $[2] = showSuccess;
    $[3] = t0;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t0 = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[36] !== t0 || $[37] !== t1 || $[38] !== t2 || $[39] !== t3 || $[40] !== t4 || $[41] !== t5 || $[42] !== t6) {
    t10 = <div className={t0}>{t1}{t2}{t3}{t4}{t5}{t6}</div>;
    $[36] = t0;
    $[37] = t1;
    $[38] = t2;
    $[39] = t3;
    $[40] = t4;
    $[41] = t5;
    $[42] = t6;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== t10 || $[45] !== t7 || $[46] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[44] = t10;
    $[45] = t7;
    $[46] = t8;
    $[47] = t11;
  } else {
    t11 = $[47];
  }
  let t12;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <h2 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">Straight to Your Bank. Zero Hassle.</h2>;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <span className="font-semibold text-foreground">Coins</span>;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  let t15;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <p className="text-lg leading-relaxed text-muted-foreground">Readers buy {t13} and spend them unlocking chapters, listening to audio, and subscribing to you. Those coins become real cash — sent directly to your bank via <span className="font-semibold text-foreground">Stripe</span>.</p>;
    t15 = <p className="text-sm text-muted-foreground">One flat 7% fee covers Stripe processing and platform costs. No surprises.</p>;
    $[50] = t14;
    $[51] = t15;
  } else {
    t14 = $[50];
    t15 = $[51];
  }
  let t16;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <Link href="/signup" className="inline-flex items-center gap-2 bg-accent text-white font-semibold py-3 px-6 rounded-full shadow-lg shadow-accent/30 hover:opacity-90 transition-opacity">Start Earning Today<ArrowRight className="h-4 w-4" /></Link>;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex-1 max-w-lg space-y-6">{t12}{t14}{t15}<div className="flex flex-wrap gap-3 pt-2">{t16}<ArticleLink href="/articles/how-cashouts-work" className="inline-flex items-center gap-2 text-accent font-semibold py-3 px-6 rounded-full border border-accent/30 hover:bg-accent/5 transition-colors">How Cashouts Work<ArrowRight className="h-4 w-4" /></ArticleLink></div></div>;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  let t18;
  if ($[54] !== t11 || $[55] !== t9) {
    t18 = <section className={t9}>{t11}{t17}</section>;
    $[54] = t11;
    $[55] = t9;
    $[56] = t18;
  } else {
    t18 = $[56];
  }
  return t18;
}